import { useAuth } from "@clerk/react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BuilderSelector } from "@/features/blueprints/BuilderSelector";
import { createQuickBlueprintDraft } from "@/features/blueprints/blueprintApi";
import {
  BLUEPRINT_INPUT_MAX_LENGTH,
  type TargetBuilder,
} from "@/features/blueprints/blueprintTypes";
import {
  QUICK_BLUEPRINT_INPUT_MIN,
  QUICK_BLUEPRINT_TITLE_MAX,
  validateQuickBlueprintForm,
} from "@/features/blueprints/blueprintStudioValidation";

type StudioMode = "quick" | "guided";

const SAMPLE_PROMPTS = [
  "A client portal where coaches can share resources, track client goals and receive weekly check-in submissions from clients.",
  "An admin-managed directory where the owner adds advertising tools and resources, and public users can browse, filter, search and bookmark them.",
  "A booking tool where service providers set their availability and clients can view open slots and book appointments online.",
];

export function BlueprintStudio() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<StudioMode>(
    searchParams.get("mode") === "guided" ? "guided" : "quick",
  );

  const [title, setTitle] = useState("");
  const [inputText, setInputText] = useState("");
  const [targetBuilder, setTargetBuilder] = useState<TargetBuilder>("universal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const inputTrimmedLength = inputText.trim().length;
  const inputTooShort = inputTrimmedLength > 0 && inputTrimmedLength < QUICK_BLUEPRINT_INPUT_MIN;

  function handleSamplePrompt(prompt: string) {
    setInputText(prompt);
    setFieldError(null);
    setApiError(null);
  }

  function handleModeChange(next: StudioMode) {
    setMode(next);
    setFieldError(null);
    setApiError(null);
  }

  async function handleQuickSubmit() {
    const err = validateQuickBlueprintForm(title, inputText);

    if (err) {
      setFieldError(err);
      return;
    }

    setFieldError(null);
    setApiError(null);
    setIsSubmitting(true);

    try {
      const token = await getToken();
      const response = await createQuickBlueprintDraft(token, {
        title: title.trim() || undefined,
        inputText,
        targetBuilder,
      });
      navigate(`/blueprints/${response.blueprint.id}?fromStudio=1`);
    } catch (err) {
      setApiError(
        err instanceof Error
          ? err.message
          : "Something went wrong creating your blueprint draft. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGuidedStart() {
    setFieldError(null);
    setApiError(null);
    setIsSubmitting(true);

    try {
      const token = await getToken();
      const response = await createQuickBlueprintDraft(token, {
        title: title.trim() || undefined,
        targetBuilder,
      });
      navigate(`/blueprints/${response.blueprint.id}?startGuided=1`);
    } catch (err) {
      setApiError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-full space-y-8">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-800"
      >
        ← Back to dashboard
      </Link>

      {/* Hero panel */}
      <section className="rounded-xl border border-indigo-100 bg-gradient-to-br from-white via-white to-indigo-50 p-6 shadow-sm sm:p-8">
        <span className="inline-block rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700">
          Blueprint Studio
        </span>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Create a new blueprint
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
          Describe the app you want to build. Alchemy will review the idea,
          propose build assumptions, and then generate a VIBERC-ready
          implementation prompt.
        </p>
      </section>

      {/* Mode selector */}
      <div>
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-slate-950">Choose how to start</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {/* Quick Blueprint */}
          <button
            type="button"
            onClick={() => handleModeChange("quick")}
            className={[
              "flex flex-col rounded-xl border p-5 text-left shadow-sm transition",
              mode === "quick"
                ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50",
            ].join(" ")}
          >
            <span className={[
              "inline-block w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold",
              mode === "quick" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600",
            ].join(" ")}>
              Quick
            </span>
            <p className="mt-3 font-semibold text-slate-950">Quick Blueprint</p>
            <p className="mt-1.5 flex-1 text-sm leading-5 text-slate-600">
              Describe your app idea in plain English and create a structured
              draft in seconds.
            </p>
            <p className={[
              "mt-4 text-xs font-semibold",
              mode === "quick" ? "text-indigo-700" : "text-slate-400",
            ].join(" ")}>
              {mode === "quick" ? "Selected ↓" : "Select →"}
            </p>
          </button>

          {/* Guided Blueprint Interview */}
          <button
            type="button"
            onClick={() => handleModeChange("guided")}
            className={[
              "flex flex-col rounded-xl border p-5 text-left shadow-sm transition",
              mode === "guided"
                ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50",
            ].join(" ")}
          >
            <span className={[
              "inline-block w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold",
              mode === "guided" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600",
            ].join(" ")}>
              Guided
            </span>
            <p className="mt-3 font-semibold text-slate-950">
              Guided Blueprint Interview
            </p>
            <p className="mt-1.5 flex-1 text-sm leading-5 text-slate-600">
              Answer smart questions about your audience, features and scope.
              The system structures your idea for you.
            </p>
            <p className={[
              "mt-4 text-xs font-semibold",
              mode === "guided" ? "text-indigo-700" : "text-slate-400",
            ].join(" ")}>
              {mode === "guided" ? "Selected ↓" : "Select →"}
            </p>
          </button>

          {/* Improve Existing Prompt — coming next */}
          <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 opacity-50">
            <span className="inline-block w-fit rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
              Improve
            </span>
            <p className="mt-3 font-semibold text-slate-950">
              Improve Existing Prompt
            </p>
            <p className="mt-1.5 flex-1 text-sm leading-5 text-slate-600">
              Paste a weak or rough build prompt and upgrade it into a complete
              VIBE-RC implementation blueprint.
            </p>
            <p className="mt-4 text-xs font-medium text-slate-400">Coming next</p>
          </div>
        </div>
      </div>

      {/* Quick Blueprint form */}
      {mode === "quick" && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-lg font-bold tracking-tight text-slate-950">
            Step 1 — Describe your app idea
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Describe what you want to build — who it is for, what problem it
            solves and what the first version should do. After this step, Alchemy
            will propose build assumptions for you to review before generation.
          </p>

          {/* Sample prompts */}
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Example prompts — click to use
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SAMPLE_PROMPTS.map((prompt, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSamplePrompt(prompt)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-left text-xs text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {prompt.slice(0, 58)}…
                </button>
              ))}
            </div>
          </div>

          {/* Form fields */}
          <div className="mt-6 grid gap-6">
            {/* Blueprint title */}
            <div>
              <label
                htmlFor="bs-title"
                className="block text-sm font-medium text-slate-700"
              >
                Blueprint title{" "}
                <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                id="bs-title"
                type="text"
                value={title}
                maxLength={QUICK_BLUEPRINT_TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Example: Client Portal for Coaches"
                className={`mt-2 w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:ring-2 focus:ring-indigo-100 ${
                  fieldError?.field === "title"
                    ? "border-red-400 focus:border-red-400"
                    : "border-slate-300 focus:border-indigo-500"
                }`}
              />
              <div className="mt-1.5 flex justify-between text-xs text-slate-400">
                <span />
                <span>
                  {title.length} / {QUICK_BLUEPRINT_TITLE_MAX}
                </span>
              </div>
            </div>

            {/* Target builder */}
            <BuilderSelector
              value={targetBuilder}
              onChange={setTargetBuilder}
              disabled={isSubmitting}
            />

            {/* App idea */}
            <div>
              <label
                htmlFor="bs-idea"
                className="block text-sm font-medium text-slate-700"
              >
                App idea <span className="text-red-500">*</span>
              </label>
              <textarea
                id="bs-idea"
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value);
                  if (fieldError?.field === "inputText") {
                    setFieldError(null);
                  }
                }}
                maxLength={BLUEPRINT_INPUT_MAX_LENGTH}
                rows={10}
                placeholder="Describe your app idea: what it does, who it is for and what problem it solves."
                className={`mt-2 w-full resize-y rounded-md border bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:ring-2 focus:ring-indigo-100 ${
                  fieldError?.field === "inputText"
                    ? "border-red-400 focus:border-red-400"
                    : "border-slate-300 focus:border-indigo-500"
                }`}
              />
              <div className="mt-2 flex items-start justify-between gap-4 text-xs">
                <span
                  className={
                    inputTooShort ? "text-amber-600" : "text-slate-500"
                  }
                >
                  {inputTooShort
                    ? `${QUICK_BLUEPRINT_INPUT_MIN - inputTrimmedLength} more character${
                        QUICK_BLUEPRINT_INPUT_MIN - inputTrimmedLength === 1 ? "" : "s"
                      } needed`
                    : "The more context you include, the stronger your blueprint will be."}
                </span>
                <span className="shrink-0 text-slate-400">
                  {inputText.length} / {BLUEPRINT_INPUT_MAX_LENGTH}
                </span>
              </div>
            </div>
          </div>

          {/* Field validation error */}
          {fieldError ? (
            <div
              role="alert"
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {fieldError.message}
            </div>
          ) : null}

          {/* API error */}
          {apiError ? (
            <div
              role="alert"
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {apiError}
            </div>
          ) : null}

          {/* Submit row */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => void handleQuickSubmit()}
              disabled={isSubmitting}
              className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {isSubmitting ? "Saving…" : "Review build assumptions →"}
            </button>
            <p className="text-xs text-slate-500">
              Next: review proposed build assumptions before generating.
            </p>
          </div>
        </section>
      )}

      {/* Guided Blueprint Interview start */}
      {mode === "guided" && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-lg font-bold tracking-tight text-slate-950">
            Guided Blueprint Interview
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Answer five focused questions — your target user, core problem,
            key features, scope and success criteria. Alchemy structures your
            answers into a high-quality blueprint brief before generating.
          </p>

          <div className="mt-6 grid gap-6">
            {/* Optional title */}
            <div>
              <label
                htmlFor="guided-title"
                className="block text-sm font-medium text-slate-700"
              >
                Blueprint title{" "}
                <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                id="guided-title"
                type="text"
                value={title}
                maxLength={QUICK_BLUEPRINT_TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Example: Coaching Client Portal"
                className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <BuilderSelector
              value={targetBuilder}
              onChange={setTargetBuilder}
              disabled={isSubmitting}
            />
          </div>

          {/* API error */}
          {apiError ? (
            <div
              role="alert"
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {apiError}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => void handleGuidedStart()}
              disabled={isSubmitting}
              className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {isSubmitting ? "Starting…" : "Start interview →"}
            </button>
            <p className="text-xs text-slate-500">
              Takes about 3 minutes. Your progress is saved automatically.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
